import { css } from '@emotion/css';
import { TextField } from 'dg-web-shared/tb-ui/inputs/TextField.ts';
import {
    LabeledToggle,
    ToggleType,
} from 'dg-web-shared/tb-ui/toggle/LabeledToggle.tsx';
import {
    FullWidthBottomButton,
    FullWidthBottomButtonColor,
} from '../../common/components/FullWidthBottomButton';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { HasEverLoggedInState } from '../../pages/home-unauthenticated/HasEverLoggedInState';
import { Colors } from 'dg-web-shared/ui/vars';
import {
    PasswordStrengthIndicator,
    passwordToShort,
} from '../../account/current-login/PasswordEditForm';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { Turnstile } from '@marsidev/react-turnstile';
import { TurnstileAction } from 'dg-web-shared/lib/TurnstileActions';
import { type JSX, useState } from 'react';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { device, DEVICE_UUID } from '../../utils/ActionLog.ts';
import { useUpdate } from 'dg-web-shared/lib/Flux';
import {
    isFieldGlobalViolationEntry,
    ValidationData,
} from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { TosLink } from '../../legal/TosFiles.tsx';
import { PrivacyLink } from '../../legal/PrivacyPolicyFiles.tsx';
import { validate } from 'email-validator';
import { RELEASE_ID } from 'dg-web-shared/common/utils/release';

interface SignupSuccessProps {
    activationToken?: string;
}

interface SignupState {
    email?: string;
    password?: string;
    passwordRepeated?: string;
    tosAccepted: boolean;
    showErrors: boolean;
    subscribeToNewsletter: boolean;
}

function ActivationToken({ activationToken }: SignupSuccessProps) {
    if (window.location.host === 'parkingpay.ch' || !activationToken) {
        return null;
    }
    return (
        <div
            className={css({
                ...Typo.body,
                padding: '25px',
                color: Colors.blue,
            })}
        >
            <a
                className={css({
                    color: Colors.blue,
                    textDecoration: 'underline',
                })}
                href={`/account-activated/${activationToken}`}
            >
                <Localized
                    de="Account aktivieren"
                    fr={'Activer le compte'}
                    it={'Attiva conto'}
                    en={'Activate account'}
                />
            </a>
        </div>
    );
}

function SignupSuccess({ activationToken }: SignupSuccessProps) {
    return (
        <div>
            <div
                className={css({
                    ...Typo.body,
                    padding: '25px',
                    color: Colors.blue,
                })}
            >
                <Localized
                    de="Sie haben sich erfolgreich registriert. Sie werden in Kürze eine E-Mail zur Aktivierung ihres Kontos erhalten."
                    fr={
                        'Vous vous êtes enregistré avec succès. Vous recevrez prochainement un e-mail pour activer votre compte.'
                    }
                    it={
                        'La registrazione si è conclusa con successo. A breve riceverà un e-mail per attivare il suo conto.'
                    }
                    en={
                        'You have successfully registered. You will shortly receive an email to activate your account.'
                    }
                />
            </div>
            <ActivationToken activationToken={activationToken} />
        </div>
    );
}

const passwordFieldValid = (password?: string) =>
    typeof password !== 'undefined' && password.length > 0;

const formValid = (
    signup: SignupState,
    passwordErrorText: string | JSX.Element | null,
) =>
    signup.tosAccepted &&
    Boolean(signup.email) &&
    validate(signup.email!) &&
    passwordFieldValid(signup.password) &&
    passwordFieldValid(signup.passwordRepeated) &&
    signup.password === signup.passwordRepeated &&
    passwordErrorText === null;

function RequiredFieldText() {
    return (
        <Localized
            de="Pflichtfeld"
            fr="Champ obligatoir"
            it="Campo obbligatorio"
            en="Mandatory field"
        />
    );
}

export function Signup({
    showLoginHint,
    close,
}: {
    showLoginHint: boolean;
    close: () => void;
}) {
    const update = useUpdate();
    const { language } = useLanguage();
    const [turnstile, setTurnstile] = useState<{
        turnstileToken: string | null;
        turnstileValid: boolean;
    }>({
        turnstileToken: null,
        turnstileValid: false,
    });

    const [signupState, setSignupState] = useState<SignupState>({
        tosAccepted: false,
        showErrors: false,
        subscribeToNewsletter: true,
    });

    const [signupRequest, submitSignup] = useServerWrite<
        {
            email: string;
            password: string;
            language: string;
            subscribeToNewsletter: boolean;
            turnstile: string;
            appVersion: string;
            uuid: string;
            device: object;
        },
        { customerId: number; activationToken?: string },
        ValidationData
    >(() => ({
        url: '/ui-api/customer-account/signup/create-account',
    }));
    useServerSuccessEffect(signupRequest, () =>
        update(store =>
            HasEverLoggedInState.stateWrite(store, { hasEver: true }),
        ),
    );
    useServerErrorEffect(signupRequest, () => {
        setSignupState({
            ...signupState,
            showErrors: true,
        });
    });

    const newPasswordErrorText = !signupState.password ? (
        <RequiredFieldText />
    ) : (
        passwordToShort(signupState.password)
    );

    const email = signupState.email;
    const passwordRepeated = signupState.passwordRepeated;
    const emailFieldViolation =
        signupRequest.status === RequestStatus.ERROR &&
        signupRequest.data?.violations
            ?.filter(violation => isFieldGlobalViolationEntry(violation))
            ?.find(violaton => violaton.fieldName === 'email')
            ?.humanReadableMessage?.byLanguage[language];

    if (signupRequest.status === RequestStatus.SUCCESS) {
        return (
            <SignupSuccess
                activationToken={signupRequest.data.activationToken}
            />
        );
    }

    return (
        <div
            className={css({
                padding: '25px',
            })}
        >
            <TextField
                tabIndex={1}
                labelText={
                    <Localized
                        de={'E-Mail'}
                        fr={'E-mail'}
                        it={'E-mail'}
                        en={'E-mail'}
                    />
                }
                inputType="email"
                name="email"
                required
                autoFocus
                autocomplete="email"
                value={email ?? ''}
                onChange={v => setSignupState({ ...signupState, email: v })}
                errorText={
                    signupState.showErrors ? (
                        signupRequest.status === RequestStatus.ERROR &&
                        emailFieldViolation ? (
                            emailFieldViolation
                        ) : typeof email === 'undefined' ||
                          email?.length === 0 ? (
                            <RequiredFieldText />
                        ) : !validate(email) ? (
                            <Localized
                                de={'Diese Email-Adresse ist ungültig.'}
                                fr={"L'adresse e-mail n'est pas valide."}
                                it={"L'indirizzo e-mail non é valido."}
                                en={'The e-mail address is not valid.'}
                            />
                        ) : null
                    ) : null
                }
            />
            <TextField
                tabIndex={2}
                inputType="password"
                required
                labelText={
                    <Localized
                        de={'Passwort'}
                        fr={'Mot de passe'}
                        it={'Password'}
                        en={'Password'}
                    />
                }
                value={signupState.password ?? ''}
                onChange={v => setSignupState({ ...signupState, password: v })}
                errorText={signupState.showErrors ? newPasswordErrorText : null}
            />
            <TextField
                tabIndex={3}
                inputType="password"
                required
                labelText={
                    <Localized
                        de={'Passwort bestätigen'}
                        fr={'Confirmer le mot de passe'}
                        it={'Conferma password'}
                        en={'Confirm password'}
                    />
                }
                value={passwordRepeated ?? ''}
                onChange={v =>
                    setSignupState({ ...signupState, passwordRepeated: v })
                }
                errorText={
                    signupState.showErrors ? (
                        typeof passwordRepeated === 'undefined' ||
                        passwordRepeated.length === 0 ? (
                            <RequiredFieldText />
                        ) : passwordRepeated !== signupState.password ? (
                            <Localized
                                de={'E-Mail-Adressen stimmen nicht überein.'}
                                fr={'Les adresses e-mail ne correspondent pas.'}
                                it={
                                    'I due indirizzi e-mail inseriti non corrispondono.'
                                }
                                en={
                                    'The two e-mail addresses you entered do not match.'
                                }
                            />
                        ) : null
                    ) : null
                }
            />
            <PasswordStrengthIndicator
                password={signupState.password || null}
            />
            <LabeledToggle
                label={
                    <Localized
                        de="Newsletter abonnieren (1-2 Mal pro Monat)"
                        fr="S'abonner à la newsletter (1-2 fois par mois)"
                        it="Abbonati alla newsletter (1-2 volte al mese)"
                        en="Subscribe to newsletter (1-2 times per month)"
                    />
                }
                onClick={() =>
                    setSignupState({
                        ...signupState,
                        subscribeToNewsletter:
                            !signupState.subscribeToNewsletter,
                    })
                }
                selected={signupState.subscribeToNewsletter}
                type={ToggleType.checkbox}
            />
            <LabeledToggle
                label={
                    <Localized
                        de={
                            <span>
                                Ich bin mit den <TosLink>AGB</TosLink> und{' '}
                                <PrivacyLink>Datenschutzerklärung</PrivacyLink>{' '}
                                einverstanden.
                            </span>
                        }
                        fr={
                            <span>
                                J’accepte les <TosLink>CGV</TosLink> et la{' '}
                                <PrivacyLink>
                                    déclaration sur la protection des données
                                </PrivacyLink>
                                .
                            </span>
                        }
                        it={
                            <span>
                                Sono d&#39;accordo con le <TosLink>CG</TosLink>{' '}
                                e{' '}
                                <PrivacyLink>
                                    l&#39;informativa sulla protezione dei dati
                                </PrivacyLink>
                                .
                            </span>
                        }
                        en={
                            <span>
                                I agree with the{' '}
                                <TosLink>terms and conditions</TosLink> and{' '}
                                <PrivacyLink>privacy policy</PrivacyLink>.
                            </span>
                        }
                    />
                }
                onClick={() =>
                    setSignupState({
                        ...signupState,
                        tosAccepted: !signupState.tosAccepted,
                    })
                }
                selected={signupState.tosAccepted}
                error={signupState.showErrors && !signupState.tosAccepted}
                type={ToggleType.checkbox}
            />
            <br />
            <Turnstile
                siteKey={'0x4AAAAAABatiRYriPvAMuT9'}
                options={{
                    theme: 'light',
                    size: 'flexible',
                    action: TurnstileAction.CUSTOMER_SIGNUP,
                }}
                onError={() =>
                    setTurnstile({
                        turnstileValid: false,
                        turnstileToken: null,
                    })
                }
                onExpire={() =>
                    setTurnstile({
                        turnstileValid: false,
                        turnstileToken: null,
                    })
                }
                onSuccess={token =>
                    setTurnstile({
                        turnstileValid: true,
                        turnstileToken: token,
                    })
                }
            />
            <br />
            {signupRequest.status === RequestStatus.ERROR &&
                !emailFieldViolation && (
                    <div
                        className={css({
                            ...Typo.body,
                            padding: '0 0 25px 0',
                            color: Colors.blue,
                        })}
                    >
                        <Localized
                            de="Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal. Wenn das Problem weiterhin auftritt, melden Sie sich bei unserem Helpdesk."
                            fr="Une erreur est survenue. Merci de réessayer plus tard. Si le problème persiste, merci de l’annoncer à notre Helpdesk."
                            it="Si è verificato un errore. Vogliate riprovare più tardi. Se il problema dovesse persistere, contattate il nostro Helpdesk."
                            en="An error has occurred. Please try again later. If the problem persists, please contact our Helpdesk."
                        />
                    </div>
                )}
            <FullWidthBottomButton
                color={FullWidthBottomButtonColor.BLUE}
                onClick={() =>
                    formValid(signupState, newPasswordErrorText) &&
                    signupRequest.status !== RequestStatus.PENDING
                        ? submitSignup({
                              email: email!,
                              password: signupState.password!,
                              language: language,
                              subscribeToNewsletter:
                                  signupState.subscribeToNewsletter,
                              turnstile: turnstile.turnstileToken!,
                              device,
                              appVersion: RELEASE_ID,
                              uuid: DEVICE_UUID,
                          })
                        : setSignupState({ ...signupState, showErrors: true })
                }
                disabled={
                    !turnstile.turnstileValid ||
                    signupRequest.status === RequestStatus.PENDING
                }
                label={{
                    de: 'Registrieren',
                    fr: "S'enregistrer",
                    it: 'Registrarsi',
                    en: 'Sign up',
                }}
            />
            {showLoginHint && (
                <div
                    className={css({
                        marginTop: '50px',
                        padding: '0 48px',
                        textAlign: 'center',
                        ...Typo.robotoRegular,
                        fontSize: '15px',
                        color: Colors.blue,
                        fontWeight: 500,
                    })}
                >
                    <Localized
                        de="Haben Sie schon ein Konto?"
                        fr="Vous avez déjà un compte?"
                        it="Avete già un conto?"
                        en="Do you already have an account?"
                    />
                    <FullWidthBottomButton
                        color={FullWidthBottomButtonColor.WHITE}
                        onClick={() => {
                            update(store =>
                                HasEverLoggedInState.stateWrite(store, {
                                    hasEver: true,
                                }),
                            );
                            close();
                        }}
                        label={{
                            de: 'Hier anmelden',
                            fr: 'Connectez-vous ici',
                            it: 'Accedete qui',
                            en: 'Sign in here',
                        }}
                    />
                </div>
            )}
        </div>
    );
}
